import { useState } from "react";
import { X, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploadField } from "@/components/ImageUploadField";
import { TimezoneSelect } from "@/components/TimezoneSelect";
import { useLang, CATEGORIES } from "@/lib/i18n";
import { toHijri } from "@/lib/hijri";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const AdminSettings = ({ assoc, onUpdate }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: assoc.name, description: assoc.description || "", logo: assoc.logo || "",
    banner: assoc.banner || "", primary_color: assoc.primary_color || "#2563EB",
    language: assoc.language || "fr", currency: assoc.currency || "EUR",
    timezone: assoc.timezone || "Europe/Paris", country: assoc.country || "",
    city: assoc.city || "", hijri_offset: assoc.hijri_offset ?? 0,
    event_categories: assoc.event_categories || [],
    category_colors: assoc.category_colors || {},
  });
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");

  const addCategory = () => {
    const label = newCat.trim().slice(0, 40);
    if (!label) return;
    const lower = label.toLowerCase();
    const clash = form.event_categories.some((c) => c.toLowerCase() === lower)
      || CATEGORIES.some((c) => t(`cat_${c}`).toLowerCase() === lower);
    if (clash) return setNewCat("");
    setForm((f) => ({ ...f, event_categories: [...f.event_categories, label] }));
    setNewCat("");
  };

  const removeCategory = (label) =>
    setForm((f) => {
      const cc = { ...f.category_colors };
      delete cc[label];
      return { ...f, event_categories: f.event_categories.filter((c) => c !== label), category_colors: cc };
    });

  const setCatColor = (key, color) =>
    setForm((f) => ({ ...f, category_colors: { ...f.category_colors, [key]: color } }));

  const clearCatColor = (key) =>
    setForm((f) => {
      const cc = { ...f.category_colors };
      delete cc[key];
      return { ...f, category_colors: cc };
    });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const todayHijri = toHijri(new Date(), parseInt(form.hijri_offset) || 0);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/associations/${assoc.slug}`, { ...form, hijri_offset: parseInt(form.hijri_offset) });
      onUpdate(data);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-8">{t("settings")}</h1>
      <div className="space-y-5">
        <div className="space-y-1.5"><Label>{t("assoc_name")}</Label><Input data-testid="settings-name" value={form.name} onChange={set("name")} /></div>
        <div className="space-y-1.5"><Label>{t("description")}</Label><Textarea rows={3} data-testid="settings-description" value={form.description} onChange={set("description")} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t("logo_url")}</Label>
            <ImageUploadField slug={assoc.slug} value={form.logo} onChange={(v) => setForm((f) => ({ ...f, logo: v }))} testId="settings-logo" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("banner_url")}</Label>
            <ImageUploadField slug={assoc.slug} value={form.banner} onChange={(v) => setForm((f) => ({ ...f, banner: v }))} testId="settings-banner" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t("primary_color")}</Label>
            <Input type="color" data-testid="settings-color" value={form.primary_color} onChange={set("primary_color")} className="h-9 p-1" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("language")}</Label>
            <Select value={form.language} onValueChange={set("language")}>
              <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>{t("city")}</Label><Input data-testid="settings-city" value={form.city} onChange={set("city")} /></div>
          <div className="space-y-1.5"><Label>{t("country")}</Label><Input data-testid="settings-country" value={form.country} onChange={set("country")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t("timezone")}</Label>
            <TimezoneSelect value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} testId="settings-timezone" />
          </div>
          <div className="space-y-1.5"><Label>{t("currency")}</Label><Input data-testid="settings-currency" value={form.currency} onChange={set("currency")} /></div>
        </div>

        <div className="border border-accent/30 rounded-lg p-5 bg-accent/5">
          <Label className="font-heading font-bold">{t("hijri_offset")}</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-3">{t("hijri_offset_desc")}</p>
          <div className="flex items-center gap-4">
            <Select value={String(form.hijri_offset)} onValueChange={(v) => setForm((f) => ({ ...f, hijri_offset: parseInt(v) }))}>
              <SelectTrigger data-testid="settings-hijri-offset" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[-3, -2, -1, 0, 1, 2, 3].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n > 0 ? `+${n}` : n} {n === 0 ? "" : "j"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm font-mono-data text-muted-foreground" data-testid="settings-hijri-preview">
              {t("today")}: {todayHijri.label} AH
            </p>
          </div>
        </div>

        <div className="border border-border rounded-lg p-5">
          <Label className="font-heading font-bold">{t("event_categories_title")}</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-3">{t("custom_categories_hint")} {t("category_colors_hint")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {CATEGORIES.map((c) => (
              <div key={c} className="flex items-center gap-2 border border-border rounded-md px-2.5 py-1.5">
                <input
                  type="color"
                  data-testid={`cat-color-${c}`}
                  value={form.category_colors[c] || "#64748b"}
                  onChange={(e) => setCatColor(c, e.target.value)}
                  className="h-5 w-6 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <span className="text-xs flex-1 truncate">{t(`cat_${c}`)}</span>
                {form.category_colors[c] && (
                  <button type="button" onClick={() => clearCatColor(c)} data-testid={`reset-color-${c}`} className="text-muted-foreground hover:text-foreground">
                    <RotateCcw className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {form.event_categories.map((c) => (
              <div key={c} className="flex items-center gap-2 border border-accent/40 rounded-md px-2.5 py-1.5" data-testid={`custom-category-${c}`}>
                <input
                  type="color"
                  data-testid={`cat-color-${c}`}
                  value={form.category_colors[c] || "#64748b"}
                  onChange={(e) => setCatColor(c, e.target.value)}
                  className="h-5 w-6 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <span className="text-xs flex-1 truncate">{c}</span>
                {form.category_colors[c] && (
                  <button type="button" onClick={() => clearCatColor(c)} data-testid={`reset-color-${c}`} className="text-muted-foreground hover:text-foreground">
                    <RotateCcw className="h-3 w-3" />
                  </button>
                )}
                <button type="button" onClick={() => removeCategory(c)} data-testid={`remove-category-${c}`} className="text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="new-category-input"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder={t("new_category_placeholder")}
              className="h-9"
            />
            <Button type="button" variant="outline" data-testid="add-category-btn" onClick={addCategory} className="rounded-full h-9 shrink-0">
              <Plus className="h-4 w-4 mr-1.5" /> {t("add_category_btn")}
            </Button>
          </div>
        </div>

        <Button data-testid="settings-save-btn" onClick={submit} disabled={saving} className="rounded-full px-8 active:scale-95">
          {saving ? "..." : t("save")}
        </Button>
      </div>
    </div>
  );
};
